import { ErrorModal } from "@dexteel/mesf-core";
import { Autocomplete, Box, CircularProgress, TextField } from "@mui/material";
import { useState } from "react";
import type { FieldError } from "react-hook-form";
import { useStrandLossCategoriesQuery } from "../../hooks/queries/useStrandLossCatalogQueries";
import { strandLossesColors } from "../../theme/colors";

interface StrandLossCategoryAutocompleteProps {
  value: number | null;
  onChange: (value: number | null) => void;
  parentAssetId: number | null;
  fieldError?: FieldError;
}

export const StrandLossCategoryAutocomplete = ({
  value,
  onChange,
  parentAssetId,
  fieldError,
}: StrandLossCategoryAutocompleteProps) => {
  const [dismissedError, setDismissedError] = useState("");
  const {
    data: categories = [],
    isLoading,
    error,
  } = useStrandLossCategoriesQuery(parentAssetId);
  const errorMessage = error instanceof Error ? error.message : "";
  const selectedValue =
    categories.find((category) => category.StrandLossCategoryId === value) ??
    null;

  return (
    <>
      <Autocomplete
        clearOnEscape
        options={categories}
        getOptionLabel={(option) => option.StrandLossCategoryName}
        renderOption={(props, option) => (
          <li {...props}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box
                component="span"
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "common.white",
                  fontWeight: "bold",
                  mx: 1,
                  bgcolor: option.StrandLossCategoryColor,
                }}
              >
                {option.StrandLossCategoryName.charAt(0).toUpperCase()}
              </Box>
              {option.StrandLossCategoryName}
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Category"
            variant="outlined"
            fullWidth
            size="small"
            error={!!fieldError}
            helperText={fieldError?.message}
            slotProps={{
              input: {
                ...params.InputProps,
                startAdornment: value ? (
                  <Box
                    component="span"
                    sx={{
                      width: 26,
                      height: 26,
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "common.white",
                      fontWeight: "bold",
                      mx: 1,
                      bgcolor:
                        selectedValue?.StrandLossCategoryColor ??
                        strandLossesColors.categoryFallback,
                    }}
                  >
                    {selectedValue?.StrandLossCategoryName.charAt(
                      0,
                    ).toUpperCase()}
                  </Box>
                ) : null,
                endAdornment: isLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : (
                  params.InputProps.endAdornment
                ),
              },
            }}
          />
        )}
        value={selectedValue}
        onChange={(_event, newValue) =>
          onChange(newValue?.StrandLossCategoryId ?? null)
        }
      />
      <ErrorModal
        error={errorMessage === dismissedError ? "" : errorMessage}
        onHide={() => setDismissedError(errorMessage)}
        title="Error loading Strand Loss categories"
      />
    </>
  );
};
